import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";

import {
  canCancelFieldReactivation,
  canDeleteField,
  isAutoEnrolledInventoryProgram,
  useAddFieldPracticeParticipations,
  useDeleteFieldPracticeParticipations,
  useGetFarmReportingYear,
  useGetFarms,
  useTranslation,
} from "utils";

import {
  ACCOUNTING_METHODOLOGY,
  CarbonProgram,
  FIELD_MANAGEMENT_PRACTICE_SLUG,
  FieldDetails as FieldDetailsType,
  FieldPracticeResponse,
  PRACTICE_SLUG,
} from "models";

import { CancelFieldReactivationModal, ContextMenu, ContextMenuItemProps, Flex, Icon, IconButton, Typography } from "components";

import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";

type FCPOMenuProps = {
  availablePracticeBundles: FieldPracticeResponse["availablePracticeBundles"];
  fieldId: string;
  isEnrolled: boolean;
  setShowFieldEditDrawer: Dispatch<SetStateAction<boolean>>;
  setShowDeleteFieldModal: Dispatch<SetStateAction<boolean>>;
  carbonProgram?: CarbonProgram | null;
  isPracticeEnrollmentDisabled?: boolean;
  fieldDetails?: FieldDetailsType;
  placement?: "right" | "left" | "bottom-left" | "bottom-right";
};

/**
 * @deprecated This component is deprecated and will be removed in a future version. Use <SoilPlus3Menu></> instead.
 * TODO: Remove this after Soil+ 3.0 is done
 */
export const FCPOMenu = ({
  availablePracticeBundles,
  fieldId,
  isEnrolled,
  setShowFieldEditDrawer,
  setShowDeleteFieldModal,
  carbonProgram,
  isPracticeEnrollmentDisabled,
  fieldDetails,
  placement = "right",
}: FCPOMenuProps) => {
  const { t } = useTranslation(["farm", "onboarding", "fieldTimeline", "allFieldView"]);

  const [showContextMenu, setShowContextMenu] = useState<boolean>(false);
  const [showCancelReactivationModal, setShowCancelReactivationModal] = useState<boolean>(false);
  const isBundleActive = useCallback(
    (bundle: string) => (availablePracticeBundles.filter((pb) => pb.slug === bundle).map((pb) => pb.active) || [])?.shift() || false,
    [availablePracticeBundles]
  );

  const [toggleTillage, setToggleTillage] = useState(isBundleActive(PRACTICE_SLUG.TILLAGES));
  const [toggleFertilizer, setToggleFertilizer] = useState(isBundleActive(PRACTICE_SLUG.ORGANIC_FERTILIZERS));

  const { currentFarm } = useGetFarms();

  const { mutate: addFieldPracticeParticipations } = useAddFieldPracticeParticipations(fieldDetails?.id ? [fieldDetails?.id] : [], currentFarm?.id);
  const { mutate: deleteFieldPracticeParticipations } = useDeleteFieldPracticeParticipations(fieldDetails?.id ? [fieldDetails?.id] : [], currentFarm?.id);

  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(currentFarm?.id || null);
  const { isDataSubmitted: isDataSubmittedForCurrentReportingYear, isDataSubmissionReopened } = useSubmissionStatus(activeFarmReportingYear);
  const isEnrollmentLockedBySubmission = isDataSubmittedForCurrentReportingYear && !isDataSubmissionReopened;

  const bundleHasConfirmedData = (bundleSlug: string) => !!availablePracticeBundles.find((bundle) => bundle.slug === bundleSlug)?.hasConfirmedData;
  const bundleIsDisabled = (bundleSlug: string) => !availablePracticeBundles.find((bundle) => bundle.slug === bundleSlug)?.enrollable;
  const bundleIsDiscontinued = (bundleSlug: string) =>
    Number.isInteger(availablePracticeBundles.find((bundle) => bundle.slug === bundleSlug)?.discontinuedYear);

  const cropBundle = availablePracticeBundles.find((bundle) => bundle.slug === "crops");
  const cropsIsActive = !!cropBundle?.active;
  const fieldEnrollmentCheckboxState =
    isPracticeEnrollmentDisabled ||
    bundleIsDiscontinued(PRACTICE_SLUG.CROPS) ||
    bundleHasConfirmedData(PRACTICE_SLUG.CROPS) ||
    isEnrollmentLockedBySubmission ||
    !cropBundle?.enrollable
      ? ("disabled" as ContextMenuItemProps["state"])
      : undefined;
  const getEnrollmentTooltip = () => {
    if (bundleIsDiscontinued(PRACTICE_SLUG.CROPS)) return t("onboarding:fieldDetails.carbonProgram.enrollmentDisabledRejected");
    if (bundleHasConfirmedData(PRACTICE_SLUG.CROPS)) return t("onboarding:fieldDetails.carbonProgram.expectedPayoutTooltipVerified");
    return undefined;
  };
  const enrollmentTooltip = getEnrollmentTooltip();

  const tillageBundle = availablePracticeBundles.find((bundle) => bundle.slug === PRACTICE_SLUG.TILLAGES);
  const tillageIsActive = !!tillageBundle?.active;
  const tillageBundleCheckboxState =
    isPracticeEnrollmentDisabled ||
    bundleIsDisabled(PRACTICE_SLUG.TILLAGES) ||
    bundleIsDiscontinued(PRACTICE_SLUG.TILLAGES) ||
    !cropsIsActive ||
    bundleHasConfirmedData(PRACTICE_SLUG.TILLAGES) ||
    isDataSubmittedForCurrentReportingYear ||
    !tillageBundle?.enrollable
      ? ("disabled" as ContextMenuItemProps["state"])
      : undefined;

  const fertilizerBundle = availablePracticeBundles.find(
    (bundle) => bundle.slug === PRACTICE_SLUG.ORGANIC_FERTILIZERS || bundle.slug === PRACTICE_SLUG.MINERAL_FERTILIZERS
  );
  const fertilizerIsActive = !!fertilizerBundle?.active;
  const fertilizerBundleCheckboxState =
    isPracticeEnrollmentDisabled ||
    bundleIsDisabled(PRACTICE_SLUG.ORGANIC_FERTILIZERS) ||
    bundleIsDiscontinued(PRACTICE_SLUG.ORGANIC_FERTILIZERS) ||
    !cropsIsActive ||
    bundleHasConfirmedData(PRACTICE_SLUG.ORGANIC_FERTILIZERS) ||
    isDataSubmittedForCurrentReportingYear ||
    !fertilizerBundle?.enrollable
      ? ("disabled" as ContextMenuItemProps["state"])
      : undefined;

  const handleBundleToggle = (practiceSlug: "crops" | "tillages" | "organicFertilizers", checked: boolean) => {
    if (!fieldId) return;

    const practiceSlugMap = {
      crops: ["crops"],
      // Only needed until we remove the feature flag for Soil+ 3.0
      tillages: [PRACTICE_SLUG.TILLAGES, PRACTICE_SLUG.SEEDINGS],
      // Only needed until we remove the feature flag for Soil+ 3.0
      organicFertilizers: [PRACTICE_SLUG.ORGANIC_FERTILIZERS, PRACTICE_SLUG.MINERAL_FERTILIZERS],
    };

    const toggleAllPractices =
      // we always want to toggle everything, for inventory
      carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory ||
      // if we are unenrolling an intervention field, unenrol all practices
      (practiceSlug === PRACTICE_SLUG.CROPS && !checked);

    const practiceSlugs = toggleAllPractices ? Object.values(FIELD_MANAGEMENT_PRACTICE_SLUG) : practiceSlugMap[practiceSlug];

    if (checked) {
      addFieldPracticeParticipations([{ fieldId, practiceSlugs }]);
    } else {
      deleteFieldPracticeParticipations({ fieldId, practiceSlugs });
    }
  };

  const contextMenuOnToggle = (bundleSlug: "crops" | "tillages" | "organicFertilizers", value: boolean) => {
    if (bundleSlug === PRACTICE_SLUG.TILLAGES) setToggleTillage(!toggleTillage);
    if (bundleSlug === PRACTICE_SLUG.ORGANIC_FERTILIZERS) setToggleFertilizer(!toggleFertilizer);
    handleBundleToggle(bundleSlug, value);
  };

  const isFieldDeletable = canDeleteField({
    availablePracticeBundles,
    carbonProgram,
  });
  const isReactivationCancellable = canCancelFieldReactivation(availablePracticeBundles, activeFarmReportingYear);

  useEffect(() => {
    setToggleTillage(isBundleActive(PRACTICE_SLUG.TILLAGES));
    setToggleFertilizer(isBundleActive(PRACTICE_SLUG.ORGANIC_FERTILIZERS));
  }, [availablePracticeBundles, isBundleActive]);

  return (
    <>
      <ContextMenu
        className="field-timeline"
        dataTestId="field-timeline-context-menu"
        items={[
          ...(isAutoEnrolledInventoryProgram(carbonProgram)
            ? []
            : [
                {
                  hasCheckbox: true,
                  iconName: "tabler/layout-grid-add",
                  isChecked: isEnrolled,
                  label: isEnrolled ? (
                    <Flex align="center" gap="0.25rem">
                      <Typography variant="paragraph-m">{t("farm:contextMenu.fieldEnrolled")}</Typography>
                      <Icon iconName="tabler/info-circle" className="icon inline" size="s" />
                    </Flex>
                  ) : (
                    <Flex vertical={true} align="flex-start" justify="center" gap="0.25rem">
                      <Typography variant="paragraph-m" className="margin-0">
                        {t("farm:contextMenu.enrollFieldInProgram")}
                      </Typography>
                      <Typography variant="paragraph-s" className="margin-0">
                        {t("farm:contextMenu.enrollFieldInProgramHelperText")}
                      </Typography>
                    </Flex>
                  ),
                  state: fieldEnrollmentCheckboxState,
                  tooltip: enrollmentTooltip,
                  onCheck: (value: boolean) => contextMenuOnToggle(PRACTICE_SLUG.CROPS, value),
                  className: bundleIsDiscontinued(PRACTICE_SLUG.CROPS) ? "discontinued" : undefined,
                },
              ]),
          {
            iconName: "tabler/edit",
            label: t("farm:contextMenu.editFieldDetails"),
            onClick: () => setShowFieldEditDrawer(true),
          },

          ...(carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.intervention
            ? [
                {
                  hasToggle: true,
                  iconName: "klim/tillage",
                  isToggled: isEnrolled && tillageIsActive,
                  label: t("farm:contextMenu.addTillageAndSedding"),
                  state: tillageBundleCheckboxState,
                  onClick: () => contextMenuOnToggle(PRACTICE_SLUG.TILLAGES, !tillageIsActive),
                  onToggle: () => contextMenuOnToggle(PRACTICE_SLUG.TILLAGES, !tillageIsActive),
                  className: bundleIsDiscontinued(PRACTICE_SLUG.TILLAGES) ? "discontinued" : undefined,
                },
                {
                  hasToggle: true,
                  iconName: "klim/fertilizer",
                  isToggled: isEnrolled && fertilizerIsActive,
                  label: t("farm:contextMenu.addFertilizer"),
                  state: fertilizerBundleCheckboxState,
                  onClick: () => contextMenuOnToggle(PRACTICE_SLUG.ORGANIC_FERTILIZERS, !fertilizerIsActive), // Only needed until we remove the feature flag for Soil+ 3.0
                  onToggle: () => contextMenuOnToggle(PRACTICE_SLUG.ORGANIC_FERTILIZERS, !fertilizerIsActive), // Only needed until we remove the feature flag for Soil+ 3.0
                  className: bundleIsDiscontinued(PRACTICE_SLUG.ORGANIC_FERTILIZERS) ? "discontinued" : undefined,
                },
              ]
            : []),
          ...(isReactivationCancellable
            ? [
                {
                  iconName: "tabler/circle-x",
                  label: t("farm:contextMenu.cancelReactivation"),
                  onClick: () => {
                    setShowContextMenu(false);
                    setShowCancelReactivationModal(true);
                  },
                  state: "warning" as ContextMenuItemProps["state"],
                },
              ]
            : []),
          {
            iconName: "tabler/trash",
            label: t("farm:contextMenu.deleteField"),
            onClick: () => setShowDeleteFieldModal?.(true),
            state: isFieldDeletable ? ("warning" as ContextMenuItemProps["state"]) : "disabled",
          },
        ]}
        placement={placement}
        iconSize="m-light"
        visible={showContextMenu}
        onClose={() => setShowContextMenu(false)}
      >
        <div
          className="context-menu-click-target"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            setShowContextMenu(true);
          }}
        >
          <IconButton
            className="header-dots-vertical-icon"
            iconName="tabler/dots-vertical"
            size="small"
            type="tertiary"
            strokeWidth={2}
            tooltip={{ title: t("allFieldView:fieldContextMenu.editField") }}
            dataTestId="dots-vertical-icon"
            onClick={() => setShowContextMenu(!showContextMenu)}
          />
        </div>
        {isReactivationCancellable && (
          <div onClick={(e) => e.stopPropagation()}>
            <CancelFieldReactivationModal
              fieldId={fieldId}
              fieldName={fieldDetails?.name}
              visible={showCancelReactivationModal}
              onClose={() => setShowCancelReactivationModal(false)}
            />
          </div>
        )}
      </ContextMenu>
    </>
  );
};
